import { useEffect, useState } from "react";
import type { X402DiscoveryPreview, X402Profile } from "../../lib/x402Profile";

interface Props {
  profile: X402Profile;
  preview: X402DiscoveryPreview | null;
  busy: boolean;
  error: string | null;
  onLoadPreview: () => void;
  onEnable: (previewDigest: string) => void;
  onDisable: () => void;
}

export function X402DiscoveryConsent(props: Props) {
  const [accepted, setAccepted] = useState(false);
  const privateOnlyMembers = props.profile.wallets.filter((wallet) =>
    !(wallet.isController || wallet.accountId.toLowerCase() === props.profile.controllerAccount?.toLowerCase()) &&
    wallet.discoveryConsent !== "public_all",
  );

  useEffect(() => setAccepted(false), [props.preview?.digest]);

  return (
    <section className="py-8" aria-labelledby="wallet-discovery-heading">
      <div className="max-w-3xl">
        <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Step 2 · Public discovery</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h2 id="wallet-discovery-heading" className="text-3xl font-display tracking-tight">Review the public scope</h2>
          <span className={`pill btn-mono text-[10px] ${props.profile.discoveryMode === "public_all" ? "pill-olive" : "pill-faint"}`}>
            {props.profile.discoveryMode === "public_all" ? "Public" : "Private"}
          </span>
        </div>
        <p className="mt-3 text-sm text-muted leading-relaxed">
          Discovery is one profile-wide choice. When it is on, all linked wallets and every supported x402 fact Boon can verify are public. There are no per-wallet exceptions.
        </p>
        <details className="mt-3 text-sm text-muted">
          <summary className="cursor-pointer min-h-11 inline-flex items-center btn-mono text-xs text-olive-deep">What publishes automatically?</summary>
          <p className="pb-2 leading-relaxed max-w-3xl">
            New exact-price Base use made with <code className="btn-mono text-xs">boon x402 fetch</code> publishes automatically. A bare onchain transfer remains only a settlement because it does not identify the HTTP route.
          </p>
        </details>
      </div>

      {props.profile.discoveryMode === "public_all" ? (
        <div className="mt-6 max-w-3xl border border-faint rounded-md p-5">
          <h3 className="text-xl font-display">Public discovery is on</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            New verified activity publishes automatically. Turning discovery off stops future Boon aggregation but cannot recall onchain records or copies already returned to others.
          </p>
          <button className="btn btn-ghost mt-4" type="button" onClick={props.onDisable} disabled={props.busy}>
            {props.busy ? "Requesting signature…" : "Turn discovery off"}
          </button>
        </div>
      ) : privateOnlyMembers.length > 0 ? (
        <div className="mt-6 max-w-3xl border border-warning/40 bg-amber-soft rounded-md p-5">
          <h3 className="text-xl font-display">A linked wallet has not accepted public discovery.</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">
            The controller cannot publish a member that joined while this profile was private. Revoke and relink that wallet after publishing the controller-only profile so the member signs the public policy itself.
          </p>
          <ul className="mt-3 space-y-1" aria-label="Wallets missing public discovery consent">
            {privateOnlyMembers.map((wallet) => <li key={wallet.accountId} className="btn-mono text-xs break-all">{wallet.accountId}</li>)}
          </ul>
        </div>
      ) : !props.preview ? (
        <button className="btn btn-ghost mt-6" type="button" onClick={props.onLoadPreview} disabled={props.busy}>
          {props.busy ? "Preparing preview…" : "Preview public profile"}
        </button>
      ) : (
        <div className="mt-6 max-w-4xl border border-faint rounded-md overflow-hidden">
          <div className="p-5 md:p-6 bg-paper-deep">
            <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-olive-deep">Public scope preview · wallet set {props.preview.walletSetVersion}</p>
            <p className="mt-3 text-base leading-relaxed">{props.preview.statement}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted">
              This preview lists exact wallet IDs and category counts. It does not show row-by-row routes, amounts, transactions, or review details.
            </p>
          </div>
          <div className="grid gap-px bg-faint border-y border-faint sm:grid-cols-2">
            <PreviewFact label="Linked wallets" value={String(props.preview.wallets.length)} />
            <PreviewFact label="Known activity facts" value={String(totalFacts(props.preview.factCounts))} />
          </div>
          <div className="p-5 md:p-6">
            <h3 className="text-xl font-display">Every wallet that will be published</h3>
            <ul className="mt-3 space-y-2" aria-label="Wallets in the public scope preview">
              {props.preview.wallets.map((accountId) => (
                <li key={accountId} className="btn-mono text-xs break-all">{accountId}</li>
              ))}
            </ul>
            {Object.keys(props.preview.factCounts).length > 0 && (
              <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                {Object.entries(props.preview.factCounts).map(([kind, count]) => (
                  <div key={kind} className="flex items-baseline gap-2"><dt className="text-xs text-muted capitalize">{kind.replaceAll("_", " ")}</dt><dd className="btn-mono text-sm">{count}</dd></div>
                ))}
              </dl>
            )}
            {props.preview.canPublishAll === false && (
              <p className="mt-5 border border-warning/40 bg-amber-soft rounded-sm p-4 text-sm leading-relaxed">
                This preview cannot be published because at least one non-controller wallet did not sign the current public discovery policy. Revoke and relink it after the controller-only profile is public.
              </p>
            )}
            <label className="mt-6 flex items-start gap-3 text-sm leading-relaxed">
              <input
                className="mt-1"
                type="checkbox"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
              />
              <span>
                Publish all linked wallets and supported verified x402 facts, now and in the future. I understand this is blanket consent, not row-by-row approval, and public copies cannot be recalled.
              </span>
            </label>
            <button
              className="btn btn-primary mt-5"
              type="button"
              disabled={!accepted || props.busy || props.preview.canPublishAll === false}
              onClick={() => props.onEnable(props.preview!.digest)}
            >
              {props.busy ? "Requesting signature…" : "Sign and publish all"}
            </button>
          </div>
        </div>
      )}

      {props.error && <p className="mt-4 text-sm text-danger" role="alert">{props.error}</p>}
    </section>
  );
}

function PreviewFact({ label, value }: { label: string; value: string }) {
  return <div className="bg-paper px-5 py-4"><dt className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">{label}</dt><dd className="mt-1 text-2xl font-display">{value}</dd></div>;
}

function totalFacts(counts: Record<string, number>): number {
  return Object.values(counts).reduce((total, count) => total + count, 0);
}
